import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { Link } from "react-router-dom";
import { Crown, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { makeApi } from "@/lib/api";
import { useT } from "@/i18n";

const TEXT = {
  es: {
    title: "Plan Familia", month: "Mensual", year: "Anual", monthPrice: "$4.98 USD / mes", yearPrice: "$49.80 USD / año", save: "¡2 meses gratis!",
    perks: ["Imágenes de IA ilimitadas en el Laboratorio (gratis: 5 al día)", "Todos los mundos y talleres para toda la familia", "Chat con Lumi y Spark", "Apoyas nuevas misiones y lecciones"],
    cta: "Suscribirme", active: "¡Tu Plan Familia está activo!", signIn: "Entra para suscribirte", error: "No se pudo completar, intenta otra vez",
    cancel: "Cancelar suscripción", confirm: "¿Seguro? Tu plan seguirá activo hasta el final del período pagado.", yes: "Sí, cancelar", no: "No, mantener",
    ends: "Tu plan termina el", cancelled: "Suscripción cancelada",
  },
  en: {
    title: "Family Plan", month: "Monthly", year: "Yearly", monthPrice: "$4.98 USD / month", yearPrice: "$49.80 USD / year", save: "2 months free!",
    perks: ["Unlimited AI images in the Lab (free: 5 per day)", "Every world and workshop for the whole family", "Chat with Lumi and Spark", "You support new missions and lessons"],
    cta: "Subscribe", active: "Your Family Plan is active!", signIn: "Sign in to subscribe", error: "Couldn't complete it, try again",
    cancel: "Cancel subscription", confirm: "Are you sure? Your plan stays active until the end of the paid period.", yes: "Yes, cancel", no: "No, keep it",
    ends: "Your plan ends on", cancelled: "Subscription cancelled",
  },
};

const CancelBox = ({ tx, lang, cancelAt, onCancel, busy }) => {
  const [ask, setAsk] = useState(false);
  if (cancelAt) return <p className="text-sm font-bold text-blue-950" data-testid="family-plan-ends">{tx.ends} {new Date(cancelAt).toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })}</p>;
  if (!ask) return <button type="button" onClick={() => setAsk(true)} className="text-sm font-bold text-rose-700 underline" data-testid="family-plan-cancel-btn">{tx.cancel}</button>;
  return (
    <div className="bg-white rounded-2xl p-3 shadow max-w-xs" data-testid="family-plan-cancel-confirm">
      <p className="text-sm font-semibold text-blue-950">{tx.confirm}</p>
      <div className="mt-2 flex gap-2">
        <button type="button" onClick={onCancel} disabled={busy} className="bg-rose-600 text-white rounded-full px-4 py-1.5 text-sm font-bold" data-testid="family-plan-cancel-yes">{tx.yes}</button>
        <button type="button" onClick={() => setAsk(false)} className="bg-sky-100 rounded-full px-4 py-1.5 text-sm font-bold text-blue-900" data-testid="family-plan-cancel-no">{tx.no}</button>
      </div>
    </div>
  );
};

export default function FamilyPlan() {
  const { lang } = useT();
  const tx = TEXT[lang];
  const { getToken, isSignedIn } = useAuth();
  const [profile, setProfile] = useState(null);
  const [interval, setInterval] = useState("month");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isSignedIn) return;
    const load = () => makeApi(getToken).get("/me").then((r) => setProfile(r.data.profile || {})).catch(() => {});
    load();
    window.addEventListener("coins-changed", load);
    return () => window.removeEventListener("coins-changed", load);
  }, [getToken, isSignedIn]);

  const subscribe = async () => {
    setLoading(true);
    try {
      const r = await makeApi(getToken).post("/payments/checkout", { lookup_key: interval === "year" ? "family_yearly" : "family_monthly", origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch {
      toast.error(tx.error);
      setLoading(false);
    }
  };

  const cancel = async () => {
    setLoading(true);
    try {
      const r = await makeApi(getToken).post("/payments/cancel");
      setProfile((p) => ({ ...p, plan_cancel_at: r.data.plan_cancel_at }));
      toast.success(tx.cancelled);
    } catch {
      toast.error(tx.error);
    } finally {
      setLoading(false);
    }
  };

  const coinMonth = profile?.plan === "family" && profile?.plan_source === "coins";
  const active = profile?.plan === "family" && !coinMonth;
  return (
    <section className="mt-8 bg-gradient-to-br from-yellow-200 to-amber-300 border-4 border-white rounded-[2rem] shadow-xl p-6 sm:p-8 grid md:grid-cols-[auto_1fr_auto] gap-6 items-center" data-testid="family-plan">
      <div className="bg-white rounded-full p-4 shadow-lg w-fit"><Crown className="w-12 h-12 text-amber-500" /></div>
      <div>
        <h2 className="font-fredoka text-3xl font-bold text-blue-950">{tx.title}</h2>
        {coinMonth && (
          <p className="mt-2 bg-lime-300 rounded-full px-4 py-1.5 font-bold text-green-900 w-fit" data-testid="family-plan-coin-month">
            {lang === "es" ? "Mes gratis con monedas hasta" : "Free coin month until"} {new Date(profile.plan_until).toLocaleDateString(lang === "en" ? "en-US" : "es-ES")}
          </p>
        )}
        {coinMonth && (
          <p className="mt-1 text-sm font-semibold text-blue-900" data-testid="family-plan-coin-note">
            {lang === "es" ? "Si te suscribes ahora, el cobro empieza hoy y el mes gratis se reemplaza." : "If you subscribe now, billing starts today and replaces the free month."}
          </p>
        )}
        {!active && (
          <div className="mt-2 inline-flex bg-white/70 rounded-full p-1" data-testid="family-plan-interval">
            {["month", "year"].map((i) => (
              <button key={i} type="button" onClick={() => setInterval(i)} className={`rounded-full px-4 py-1.5 text-sm font-bold ${interval === i ? "bg-blue-600 text-white" : "text-blue-900"}`} data-testid={`family-plan-${i}`}>{tx[i]}</button>
            ))}
          </div>
        )}
        <p className="mt-2 font-fredoka text-xl font-bold text-blue-800" data-testid="family-plan-price">
          {interval === "year" && !active ? tx.yearPrice : active && profile?.plan_interval === "year" ? tx.yearPrice : tx.monthPrice}
          {interval === "year" && !active && <span className="ml-2 bg-lime-300 text-green-900 rounded-full px-2 py-0.5 text-sm" data-testid="family-plan-save">{tx.save}</span>}
        </p>
        <ul className="mt-2 space-y-1">
          {tx.perks.map((p) => <li key={p} className="flex gap-2 font-semibold text-blue-950"><Check className="w-5 h-5 text-green-700 shrink-0" />{p}</li>)}
        </ul>
      </div>
      {active ? (
        <div className="space-y-3">
          <p className="bg-lime-300 rounded-full px-6 py-3 font-fredoka font-bold text-green-900 text-lg" data-testid="family-plan-active">{tx.active}</p>
          <CancelBox tx={tx} lang={lang} cancelAt={profile.plan_cancel_at} onCancel={cancel} busy={loading} />
        </div>
      ) : isSignedIn ? (
        <button type="button" onClick={subscribe} disabled={loading} className="glossy-btn bg-blue-600 text-white font-fredoka font-bold text-xl px-8 py-3 rounded-full inline-flex items-center gap-2 disabled:opacity-70" data-testid="family-plan-subscribe-btn">
          {loading && <RefreshCw className="w-5 h-5 animate-spin" />} {tx.cta}
        </button>
      ) : (
        <Link to="/sign-in" className="glossy-btn inline-block bg-white text-blue-900 font-bold px-6 py-3 rounded-full" data-testid="family-plan-signin">{tx.signIn}</Link>
      )}
    </section>
  );
}
